'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';



const navLinks = [
  { label: 'EMD Program', href: '/emd-program' },
  { label: 'Deals We Fund', href: '/industries' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const firstMenuLinkRef = useRef<HTMLAnchorElement>(null);
  const menuOverlayRef = useRef<HTMLDivElement>(null);

  const isHomepage = pathname === '/homepage' || pathname === '/';
  const hasSolidBg = scrolled || !isHomepage;

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll, manage focus, and apply inert to background content when menu opens/closes
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';

      const menuEl = menuOverlayRef.current;
      if (menuEl) {
        // Identify which top-level document.body child contains the menu overlay.
        // Never apply inert to that element or any of its ancestors.
        const bodyChildren = Array.from(document.body.children);
        bodyChildren.forEach((el) => {
          const containsMenu = el === menuEl || el.contains(menuEl);
          if (!containsMenu) {
            (el as HTMLElement).setAttribute('inert', '');
            (el as HTMLElement).setAttribute('data-menu-inert', 'true');
          }
        });
      }

      // Move focus to first nav link after inert state is correctly applied
      requestAnimationFrame(() => {
        firstMenuLinkRef.current?.focus();
      });
    } else {
      document.body.style.overflow = '';

      // Remove only inert attributes that this mobile-menu logic added
      document.querySelectorAll('[data-menu-inert="true"]').forEach((el) => {
        (el as HTMLElement).removeAttribute('inert');
        (el as HTMLElement).removeAttribute('data-menu-inert');
      });
    }
    return () => {
      document.body.style.overflow = '';
      // Cleanup on unmount — remove only inert attributes tagged by this logic
      document.querySelectorAll('[data-menu-inert="true"]').forEach((el) => {
        (el as HTMLElement).removeAttribute('inert');
        (el as HTMLElement).removeAttribute('data-menu-inert');
      });
    };
  }, [menuOpen]);

  // Escape key closes the menu and returns focus to the button
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && menuOpen) {
        closeMenu();
        requestAnimationFrame(() => {
          menuButtonRef.current?.focus();
        });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen, closeMenu]);

  // Focus trap: Tab and Shift+Tab cycle within the menu overlay
  useEffect(() => {
    if (!menuOpen) return;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const menuEl = menuOverlayRef.current;
      if (!menuEl) return;

      const focusableSelectors =
        'a[href], button:not([disabled]), [tabindex]:not([tabIndex="-1"])';
      const focusableEls = Array.from(
        menuEl.querySelectorAll<HTMLElement>(focusableSelectors)
      ).filter((el) => !el.closest('[inert]'));

      if (focusableEls.length === 0) return;

      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: if focus is on first element, wrap to last
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        // Tab: if focus is on last element, wrap to first
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [menuOpen]);

  // Close menu on navigation link click — do NOT force focus back to hamburger
  // (allow normal browser/destination-page focus behavior after navigation)
  const handleNavLinkClick = useCallback(() => {
    closeMenu();
    // No forced focus return — let the browser handle focus on the new page
  }, [closeMenu]);

  // Close menu via backdrop click or close button — return focus to hamburger
  const handleMenuDismiss = useCallback(() => {
    closeMenu();
    requestAnimationFrame(() => {
      menuButtonRef.current?.focus();
    });
  }, [closeMenu]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500`}
        style={{
          background: hasSolidBg
            ? 'rgba(13, 31, 23, 0.98)'
            : 'transparent',
          borderBottom: hasSolidBg
            ? '1px solid rgba(82,183,136,0.1)'
            : '1px solid transparent',
          padding: scrolled ? '14px 0' : '22px 0',
        }}
      >
        <div className="max-w-7xl mx-auto px-8 flex items-center justify-between">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-3 group">
            <AppLogo
              src="/assets/images/EB_logo_White_160_x_160_px_-1777167253747.png"
              size={40}
              className=""
            />
            <span
              className="font-bold text-[15px] tracking-tight transition-colors duration-300"
              style={{
                color: 'rgba(255,255,255,0.95)',
                fontFamily: 'var(--font-body)',
                letterSpacing: '-0.01em',
              }}
            >
              EarnestBridge
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-9" aria-label="Primary navigation">
            {navLinks?.map((link) => {
              const isActive = mounted && pathname === link?.href;
              return (
                <Link
                  key={link?.href}
                  href={link?.href}
                  aria-current={isActive ? 'page' : undefined}
                  className="relative text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-200 group inline-flex items-center"
                  style={{
                    color: isActive
                      ? '#74C69D'
                      /* Inactive: #7a8e85 gives 4.71:1 on dark header — was #75887f (4.35:1) */
                      : 'var(--color-nav-inactive)',
                    fontFamily: 'var(--font-body)',
                    /* Ensure minimum 24px touch target height */
                    minHeight: '24px',
                    padding: '4px 0',
                  }}
                >
                  {link?.label}
                  <span
                    className="absolute -bottom-0.5 left-0 h-px transition-all duration-300"
                    aria-hidden="true"
                    style={{
                      width: isActive ? '100%' : '0%',
                      background: '#52B788',
                    }}
                  />
                </Link>
              );
            })}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-5">
            <Link
              href="/contact"
              className="text-[10px] font-bold uppercase tracking-[0.22em] px-6 py-2.5 transition-all duration-300"
              style={{
                background: '#1B4332',
                color: 'white',
                border: '1px solid rgba(82,183,136,0.25)',
                borderRadius: '6px',
                letterSpacing: '0.2em',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#0D2B1F';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#1B4332';
              }}
            >
              Get Funded
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            ref={menuButtonRef}
            className="lg:hidden flex flex-col gap-1.5 p-2 z-[60]"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
          >
            <span
              aria-hidden="true"
              className="block w-5 h-px transition-all duration-300"
              style={{
                background: 'rgba(255,255,255,0.8)',
                transform: menuOpen ? 'rotate(45deg) translate(1px, 5px)' : 'none',
              }}
            />
            <span
              aria-hidden="true"
              className="block w-5 h-px transition-all duration-300"
              style={{
                background: 'rgba(255,255,255,0.8)',
                opacity: menuOpen ? 0 : 1,
              }}
            />
            <span
              aria-hidden="true"
              className="block w-5 h-px transition-all duration-300"
              style={{
                background: 'rgba(255,255,255,0.8)',
                transform: menuOpen ? 'rotate(-45deg) translate(1px, -5px)' : 'none',
              }}
            />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay
          - hidden attribute prevents keyboard access when closed
          - inert applied to siblings (header + page content) when open
          - focus trap keeps Tab/Shift+Tab within the overlay
      */}
      <div
        ref={menuOverlayRef}
        id="mobile-menu"
        className={`fixed inset-0 z-40 transition-all duration-500 lg:hidden ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        hidden={!menuOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div
          className="absolute inset-0"
          style={{ background: 'rgba(13, 31, 23, 0.98)', backdropFilter: 'blur(16px)' }}
          onClick={handleMenuDismiss}
        />
        {/* Thin vertical rule */}
        <div
          className="absolute top-0 bottom-0 pointer-events-none"
          aria-hidden="true"
          style={{ left: '20%', width: '1px', background: 'rgba(82,183,136,0.08)' }}
        />
        <div className="relative z-10 flex flex-col justify-center h-full px-10">
          {/* Close button — inside the overlay so it is never inside an inert subtree.
              The hamburger button in <header> becomes inert when the menu opens,
              so this dedicated button is the only reliable way to close via pointer/touch. */}
          <button
            onClick={handleMenuDismiss}
            aria-label="Close navigation"
            className="absolute top-6 right-6 p-2 flex items-center justify-center"
            style={{ color: 'rgba(255,255,255,0.8)', zIndex: 10 }}
          >
            <svg
              aria-hidden="true"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line x1="2" y1="2" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <line x1="18" y1="2" x2="2" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <div className="mb-10">
            <p
              className="text-[9px] font-bold uppercase tracking-[0.4em] mb-8"
              aria-hidden="true"
              style={{ color: 'rgba(82,183,136,0.5)' }}
            >
              Navigation
            </p>
            <nav aria-label="Mobile navigation">
              {navLinks?.map((link, i) => {
                const isActive = mounted && pathname === link?.href;
                return (
                  <Link
                    key={link?.href}
                    href={link?.href}
                    ref={i === 0 ? firstMenuLinkRef : undefined}
                    onClick={handleNavLinkClick}
                    aria-current={isActive ? 'page' : undefined}
                    className="block py-4 text-2xl font-semibold transition-colors duration-200"
                    style={{
                      color: isActive ? '#74C69D' : 'rgba(255,255,255,0.75)',
                      fontFamily: 'var(--font-body)',
                      borderBottom: '1px solid rgba(82,183,136,0.07)',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {link?.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <Link
            href="/contact"
            onClick={handleNavLinkClick}
            className="inline-block text-[11px] font-bold uppercase tracking-[0.22em] px-8 py-4 text-center"
            style={{
              background: '#1B4332',
              color: 'white',
              border: '1px solid rgba(82,183,136,0.2)',
              borderRadius: '6px',
            }}
          >
            Get Funded
          </Link>
        </div>
      </div>
    </>
  );
}